'use client'

import { useMemo } from 'react'

import { env } from '@/env'
import { useAnalyzify } from 'analyzify-js/react'

import { isAnalyzifyEnabled } from './Analyzify.constants'

import type { AnalyzifyContextValue } from 'analyzify-js/react'

/** The subset of track.* we call imperatively from event handlers. */
type TrackedEvents = Pick<AnalyzifyContextValue['track'], 'selectItem' | 'viewCart' | 'beginCheckout' | 'addToCart' | 'removeFromCart'>

/**
 * useAnalyzify() that returns null instead of throwing when no provider is
 * mounted, which is the case whenever the mode is `off`.
 *
 * The throw happens *after* useContext, so hook order is unaffected and this is
 * safe to call unconditionally - which matters, because the alternative,
 * calling the hook conditionally, breaks the rules of hooks.
 */
const useOptionalAnalyzify = (): AnalyzifyContextValue | null => {
  try {
    return useAnalyzify()
  } catch {
    return null
  }
}

const noop = () => undefined

const DISABLED: TrackedEvents = {
  selectItem: noop,
  viewCart: noop,
  beginCheckout: noop,
  addToCart: noop,
  removeFromCart: noop,
}

/**
 * Tracking for call sites that fire on an interaction rather than on mount.
 *
 * The SDK ships <SelectItemTracker> and <BeginCheckoutButton> for this, but they
 * render their own markup and would mean rebuilding our product card and cart
 * button. This keeps the existing components untouched.
 *
 * Every method is a no-op until the SDK is mounted and ready, so call sites do
 * not need to guard. `track` methods are bound, so destructuring is safe.
 */
export const useAnalyzifyTracking = (): TrackedEvents & { ready: boolean } => {
  const enabled = isAnalyzifyEnabled(env.NEXT_PUBLIC_ANALYZIFY_MODE)
  const analyzify = useOptionalAnalyzify()

  const ready = enabled && analyzify?.isReady === true
  const track = analyzify?.track

  return useMemo(() => {
    if (!ready || !track) return { ready: false, ...DISABLED }

    const { selectItem, viewCart, beginCheckout, addToCart, removeFromCart } = track

    return { ready: true, selectItem, viewCart, beginCheckout, addToCart, removeFromCart }
  }, [ready, track])
}
